import SiteCoreLinks from "@/components/SiteCoreLinks";
// /thai-visa — Wave 3 Batch 3 hub (Thai) — outbound visa index
import { createFileRoute, Link } from "@tanstack/react-router";
import { OUTBOUND_DESTINATIONS, OUTBOUND_VISA_TYPES } from "@/data/thai-outbound-visas";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { Plane, Globe2, MessageCircle, Phone } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/thai-visa/")({
  head: () => {
    const url = `${SITE}/thai-visa`;
    const title = "วีซ่าไปต่างประเทศสำหรับพลเมืองไทย · แปลรับรอง + Apostille/สถานทูต | NYC";
    const desc = "รวมบริการเตรียมเอกสารวีซ่าท่องเที่ยว/นักเรียน/ทำงาน/ครอบครัว 30 ประเทศปลายทาง — เริ่มต้นง่าย ๆ แค่ทักไลน์ เราดูแลจนจบทุกขั้นตอน";
    return {
      meta: [
        { title: ctaTitle(title, "/thai-visa", "th") }, { name: "description", content: ctaDesc(desc, "/thai-visa", "th")},
        { property: "og:title", content: ctaTitle(title, "/thai-visa", "th")}, { property: "og:description", content: ctaDesc(desc, "/thai-visa", "th")},
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks("/thai-visa", "/en/thai-visa")],
      scripts: [{ type: "application/ld+json", children: JSON.stringify({ "@context":"https://schema.org","@type":"CollectionPage","@id":`${url}#collection`,"url":url,"name":title,"inLanguage":"th","numberOfItems":OUTBOUND_DESTINATIONS.length*OUTBOUND_VISA_TYPES.length }) }],
    };
  },
  component: ThaiVisaHub,
});

function ThaiVisaHub() {
  const grouped = OUTBOUND_DESTINATIONS.reduce<Record<string, typeof OUTBOUND_DESTINATIONS>>((acc, d) => {
    (acc[d.regionTh] ||= []).push(d); return acc;
  }, {});
  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Plane className="h-3.5 w-3.5" /> วีซ่าไปต่างประเทศ · 30 ประเทศ · 4 ประเภท
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            พลเมืองไทย <span className="text-gold">ขอวีซ่าไปต่างประเทศ</span>
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            แปลรับรอง MFA + Apostille หรือรับรองสถานทูต — ครบชุดพร้อมยื่น VFS/TLS/BLS/สถานทูต
          </p>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md"><MessageCircle className="h-4 w-4" /> LINE @nycli</a>
            <a href="tel:+66832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md"><Phone className="h-4 w-4" /> 083 249 4999</a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        {Object.entries(grouped).map(([region, list]) => (
          <section key={region} className="mb-12">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep">{region}</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((d) => (
                <div key={d.slug} className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="flex items-center gap-2">
                    <Globe2 className="h-4 w-4 text-amber-600" />
                    <div className="font-bold text-slate-900">{d.nameTh}</div>
                    <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${d.hague ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{d.hague ? "Hague" : "Embassy"}</span>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-1.5">
                    {OUTBOUND_VISA_TYPES.map((v) => (
                      <Link key={v.slug} to="/thai-visa/$destination/$type" params={{ destination: d.slug, type: v.slug }} className="rounded-lg border border-slate-200 px-2 py-1.5 text-[12px] font-semibold text-slate-700 hover:border-gold/50 hover:text-navy-deep">{v.labelTh}</Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      <TrustLayer variant="compact" />
          <SiteCoreLinks />
    </main>
  );
}
